/*!
 *  (c)(r) 2008-2023 IT-Service Robert Frunzke
 */

import { sprintf_0xd, sprintf_0x3 } from './sprintf-util.js';


export let MDLocaleBase = function() {

	let m_impl = null;

	// real thin space (in browser: when copying text containing this kind of space, a real space character is copied)
	const real_thinspace_x = '<span style="margin-left:0.167em"><span style="display:none">&nbsp;</span></span>';

	// NOTE: dont (yet) import this from ../util.js, for reasons ...
	function _clip( n, lower, upper ) {
		return Math.max(lower,Math.min(n,upper));
	}

	function fmt_filesize( s, plain_text, multiplier, significant_digits ) {
		if( typeof multiplier === 'undefined' ) multiplier = 1024;
		if( typeof significant_digits === 'undefined' ) significant_digits = 2;
		var sdm = Math.pow(10,_clip(significant_digits,0,8));
		var e = 'B';
		if( s >= multiplier ) {
			s = Math.round( sdm * s / multiplier ) / sdm;
			e = 'kB';
		}
		if( s >= multiplier ) {
			s = Math.round( sdm * s / multiplier ) / sdm;
			e = 'MB';
		}
		if( s >= multiplier ) {
			s = Math.round( sdm * s / multiplier ) / sdm;
			e = 'GB';
		}
		if( s >= multiplier ) {
			s = Math.round( sdm * s / multiplier ) / sdm;
			e = 'TB';
		}
		return m_impl.fmt_number(s,'',plain_text) + (plain_text?'':real_thinspace_x) + e;
	}

	function fmt_interval(iv) {
		// HH:mm:ss
		if( iv.length==8 && iv.match(/^\d\d:\d\d:\d\d$/) ) {
			let h = parseInt(iv.substr(0,2),10);
			// QUIRK: we currently just ignore minutes & seconds here
			return h+'h';
		}
		if( iv=='1 day' ) return '1d';
		if( iv.match(/^\d+ days$/) ) return parseInt(iv,10)+'d';
		// other/unknown interval syntax
		return iv;
	}


	function fmt_duration( s, with_ms, roundLastUnit ) {
		var t = '', x, min, ms;
		var roundFn = roundLastUnit ? Math.round : Math.floor;
		if( s >= 86400 ) {
			// days
			x = Math.floor(s/86400);
			s -= x*86400;
			t += x+'d';
		}
		if( t.length || s >= 3600 ) {
			// hours
			x = Math.floor(s/3600);
			s -= x*3600;
			t += x+':';
		}
		// mm:ss
		min = Math.floor(s/60);
		if( with_ms ) {
			// mm:ss.xxx (xxx=ms)
			ms = roundFn(1000*(s%60));
			t += sprintf_0xd(min,2) + ':' + sprintf_0xd(Math.floor(ms/1000),2) + '.' + sprintf_0xd(ms%1000,3);
		} else {
			// mm:ss
			t += sprintf_0xd(min,2) + ':' + sprintf_0xd(roundFn(s%60),2);
		}
		return t.length ? t : '00:00';
	}

	function fmt_duration_fancy( s, plain_text, granularity ) {
		if( typeof granularity === 'undefined' ) granularity = 2;
		var out = [];
		var iv = s;
		var i, l, u, x, p;
		for( i=0, l=m_impl.duration_units.length; i<l; ++i ) {
			u = m_impl.duration_units[i];
			if( iv > u[2] ) {
				x = Math.floor(iv/u[2]);
				iv = iv % u[2];
				p = x===1 ? (u[0]) : (x+u[1]);
				if( !plain_text ) p = p.replace(' ',real_thinspace_x);
				out.push( p );
				if( --granularity <= 0 ) break;
			}
		}
		if( out.length ) return out.join(' ');
		out = ('0'+m_impl.duration_units[m_impl.duration_units.length-1][1]);
		return plain_text ? out : out.replace(' ',real_thinspace_x);
	}

	function fmt_coordinates( deg, type, plain_text ) {
		deg = parseFloat(deg);

		let pfx = '';
		if( type==='longitude' ) pfx = deg>=0 ? 'E' : 'W';
		else if( type==='latitude' ) pfx = deg>=0 ? 'N' : 'S';

		const d = Math.floor(Math.abs(deg));
		let mmm = 60 * ( Math.abs(deg) - d );
		mmm = Math.round(1000000*mmm) / 1000000;
		const m = Math.floor(mmm);
		let s = 60 * (mmm-m);
		s = Math.round(1000*s) / 1000;

		const sepaspace = '';
		const thinspace = plain_text ? '' : real_thinspace_x;
		return pfx
			+ d + thinspace + '°'
			+ sepaspace
			+ m + thinspace + '\''
			+ sepaspace
			+ m_impl.fmt_number(s,'',plain_text) + thinspace + '"';
	}

	function get_normalized_highprec_val( n ) {
		if( n==='' ) return '';
		const fVal = parseFloat(n);
		const fixedValStr = fVal.toFixed(16);
		const hpValStr = parseFloat(fixedValStr).toPrecision();
		return hpValStr;
	}

	function fmt_highprec( n, plain_text ) {
		// const thinspace = plain_text ? '' : real_thinspace_x;
		const hpValStr = get_normalized_highprec_val(n);
		const locValStr = m_impl.fmt_number_input(hpValStr);
		return locValStr;
	}


	function wrapped_to_span_with_title( formatted_value, plain_text, title_html ) {
		return plain_text ? formatted_value : '<span title="'+title_html+'">'+formatted_value+'</span>';
	}


	function fmt_float_common_base( n, decimals, fill_decimals, fmt_digit_group_cb ) {
		if( typeof n !== 'number' ) n = parseFloat(n).toFixed(decimals);
		else n = n.toFixed(decimals);

		var a = parseInt(n,10);
		var f = n-a;				// e.g.:  100.3 - 100 = 0.3;   -100.3 - -100 = -100.3 + 100 = -0.3
		var sign = '';
		if( a < 0 || Object.is(a,-0) ) {
			sign = '-';
			a = -a;
			f = -f;
		}
		let str = [];
		do {
			const m = a % 1000;
			a = ( a / 1000 ) | 0;
			str[str.length] = a===0 ? m.toString() : fmt_digit_group_cb(sprintf_0x3(m));
		} while( a > 0 );
		str.reverse();
		f = sprintf_0xd(parseInt(Math.round(f*Math.pow(10,decimals)),10),decimals);
		if( !fill_decimals ) {
			while( f.length && f.charAt(f.length-1)==='0' ) f = f.substr(0,f.length-1);
		}
		return sign + str.join('') + ( f==='' ? '' : ( m_impl.decimal_point_char + f ) );
	}

	function fmt_float_plain( n, decimals, fill_decimals ) {
		return fmt_float_common_base(n,decimals,fill_decimals,(dstr)=>dstr);
	}

	function fmt_number( n, type, plain_text ) {
		var decimals = 5;
		if( type === 'filesize' ) return wrapped_to_span_with_title( m_impl.fmt_filesize(n,plain_text), plain_text, n+'B' );
		else if( type === 'duration' ) return wrapped_to_span_with_title( m_impl.fmt_duration(n,false,true), plain_text, m_impl.fmt_duration(n,true,true)+' / '+n+'s' );
		else if( type === 'degree' ) return m_impl.fmt_number(n,'',plain_text) + (plain_text?'':real_thinspace_x) + '°';
		else if( type === 'longitude' || type === 'latitude' ) return fmt_coordinates(n,type,plain_text);
		else if( type === 'highprec' ) return m_impl.fmt_highprec(n,plain_text);
		else if( plain_text ) return m_impl.fmt_float_plain(n,decimals,false);

		const fill_decimals = false;
		return fmt_float_common_base(n,decimals,fill_decimals,(dstr)=>'<span style="margin-left:0.125em">'+dstr+'</span>');
	}



	return {
		init_with_impl: function( impl ) {
			m_impl = impl;
		},
		get_impl: function() {
			if( m_impl===null ) throw new Error('MDLocaleBase.init_with_impl not called yet');
			return m_impl;
		},

		fmt_filesize,
		fmt_interval,

		fmt_duration,
		fmt_duration_fancy,

		fmt_coordinates,

		get_normalized_highprec_val,
		fmt_highprec,

		fmt_float_common_base,
		fmt_float_plain,

		fmt_number,

		real_thinspace_x,
	};
}(); // namespace MDLocaleBase
